<script setup lang="ts">
import type { ImageCategory } from "@/rvapi";
import type { Frame, UploadFrame } from "@/types/frames";
import type { UploadFileItem } from "@/types/frames";
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { PnFileUpload } from "@postnord/web-components/components/pn-file-upload";
import { useFrames } from "@/stores/frames";
import { useImageStore } from "@/stores/images";
import { storeToRefs } from "pinia";

const router = useRouter();
const route = useRoute();
const base64 = ref("");
const filename = ref("");
const frameGroupId = route.params.id as string;
const firstFrame = ref<Frame>({} as Frame);
const categories = ref<Record<number, ImageCategory>>({});
const selectedCategory = ref<ImageCategory | undefined>();
const saving = ref(false);
const saveReminder = ref(false);
const swedishAltText = ref("");
const englishAltText = ref("");
const isError = ref(false);
const framesStore = useFrames();
const imageStore = useImageStore();
const currentFrameId = ref(0);
const englishAltTextError = ref("");
const swedishAltTextError = ref("");
const showSpinner = ref(false);
const { framesForGroup } = storeToRefs(framesStore);
const fileMap = ref(new Map<number, UploadFileItem>());
const initialSwedishAltText = ref("");
const initialEnglishAltText = ref("");
const initialCategory = ref<ImageCategory | undefined>();
const intialLanguages = ref<string[]>([]);

const getImageUrl = (frame: Frame) => {
  if (fileMap.value.has(frame.frameId)) {
    const file = fileMap.value.get(frame.frameId);
    return `data:image/png;base64,${file?.base64}`;
  }
  return frame.thumbUrl;
};

const filesAdded = (e: any, frameId: number) => {
  currentFrameId.value = frameId;
  if (e.detail.length > 0) {
    const errorFiles = e.detail.filter((d: any) => d.error);

    if (errorFiles.length > 0) {
      errorFiles.forEach((d: any) => {
        e.target.removeFile(d);
      });
    } else {
      fileMap.value.set(currentFrameId.value, e.detail[0]);
    }
    saveReminder.value = true;
  }
};

const uploadFile = async (frameId: number) => {
  try {
    showSpinner.value = true;
    const uploadObj = {} as UploadFrame;
    uploadObj.hidden = firstFrame.value.hidden;
    uploadObj.languages = firstFrame.value.languages;
    uploadObj.groupId = firstFrame.value.groupId ?? "";

    if (base64.value) {
      uploadObj.base64 = base64.value;
      uploadObj.filename = filename.value;
    }
    if (fileMap.value.has(frameId)) {
      const file = fileMap.value.get(frameId);
      uploadObj.base64 = file?.base64 ?? "";
      uploadObj.filename = file?.filename ?? "";
    }
    if (selectedCategory.value) {
      uploadObj.categories = [selectedCategory.value.categoryId];
    }
    uploadObj.imageDescription = [
      {
        language: "swedish",
        description: swedishAltText.value,
      },
      {
        language: "english",
        description: englishAltText.value,
      },
    ];
    await framesStore.updateFrame(String(frameId), uploadObj);
  } catch (e: any) {
    isError.value = true;
    framesStore.setNotificationAlert({
      show: true,
      type: "error",
      message: "There was an error in uploading one or more frames",
      autoHide: true,
    });
  } finally {
    showSpinner.value = false;
  }
};

const uploadAllFiles = async () => {
  resetError();
  const isValidationError =
    swedishAltText.value === "" || englishAltText.value === "";
  if (swedishAltText.value === "") {
    swedishAltTextError.value = "Please enter swedish alternative text";
  } else {
    swedishAltTextError.value = "";
  }
  if (englishAltText.value === "") {
    englishAltTextError.value = "Please enter english alternative text";
  } else {
    englishAltTextError.value = "";
  }

  if (!isValidationError) {
    for (const frame of framesForGroup.value) {
      await uploadFile(frame.frameId);
    }
    if (!isError.value) {
      framesStore.setNotificationAlert({
        show: true,
        message: "Frames updated successfully",
        type: "success",
        autoHide: true,
      });
    }
    router.push("/frames");
  }
};

const languageChange = (e: any) => {
  const lang = e.target.id;
  const prevState = firstFrame.value.languages.includes(lang);
  if (prevState !== e.target.checked) {
    if (e.target.checked) {
      firstFrame.value.languages.push(lang);
    } else {
      firstFrame.value.languages = firstFrame.value.languages.filter(
        (l) => l !== lang,
      );
    }
  }
};

const canSave = computed(() => {
  return (
    !arraysEqual(intialLanguages.value, firstFrame.value.languages) ||
    selectedCategory.value !== initialCategory.value ||
    swedishAltText.value !== initialSwedishAltText.value ||
    englishAltText.value !== initialEnglishAltText.value ||
    fileMap.value.size > 0
  );
});

function arraysEqual(array1: string[], array2: string[]) {
  if (array1 && array2) {
    return (
      array1.length === array2.length &&
      array1.every((value, index) => value === array2[index])
    );
  } else {
    return true;
  }
}

const refreshCategories = async () => {
  const result = await imageStore.getImageCategories();
  for (const c of result) {
    categories.value[c.categoryId] = c;
  }
};
const setSelectedCategory = (frame: Frame) => {
  if (frame.categories && frame.categories.length) {
    const cat = frame.categories[0];
    if (cat) selectedCategory.value = categories.value[cat];
  }
};

onMounted(async () => {
  try {
    showSpinner.value = true;
    resetError();
    await refreshCategories();
    await framesStore.getFramesByGroupId(frameGroupId);
    firstFrame.value = framesForGroup.value[0];
    setSelectedCategory(framesForGroup.value[0]);
    swedishAltText.value = getAltText("swedish");
    englishAltText.value = getAltText("english");
    initialEnglishAltText.value = String(englishAltText.value);
    initialSwedishAltText.value = String(swedishAltText.value);
    initialCategory.value = selectedCategory.value;
    intialLanguages.value = [...framesForGroup.value[0].languages];
  } catch {
    framesStore.setNotificationAlert({
      show: true,
      type: "error",
      autoHide: true,
      message:
        "There was an error loading the frames in the group.Please try again",
    });
    router.push("/frames");
  } finally {
    showSpinner.value = false;
  }
});

const getAltText = (language: string) => {
  let description = "";
  if (firstFrame?.value?.imageDescription?.length > 0) {
    const imageDescriptionArr = firstFrame.value.imageDescription;
    const descriptionObj = imageDescriptionArr.find((imageDescObj) => {
      return imageDescObj.language === language;
    });
    if (descriptionObj) {
      description = descriptionObj.description;
    }
  }
  return description;
};

const resetError = () => {
  framesStore.setNotificationAlert({
    show: false,
    message: "",
    type: "error",
    autoHide: false,
  });
};
</script>

<template>
  <h2>Edit frame Group Id : {{ frameGroupId }}</h2>
  <div :class="[{ spinner: showSpinner }]">
    <pn-spinner size="3" v-show="showSpinner" />
  </div>
  <h3>Visible for language:</h3>
  <p>
    <label for="swedish">
      <pn-checkbox
        checkboxid="swedish"
        :checked="
          framesForGroup[0]?.languages &&
          framesForGroup[0].languages.includes('swedish')
        "
        @change="languageChange"
      />
      Swedish
    </label>
    <label for="english">
      <pn-checkbox
        checkboxid="english"
        :checked="
          framesForGroup[0]?.languages &&
          framesForGroup[0].languages.includes('english')
        "
        @change="languageChange"
      />
      English
    </label>
    <label for="danish">
      <pn-checkbox
        checkboxid="danish"
        :checked="
          framesForGroup[0]?.languages &&
          framesForGroup[0].languages.includes('danish')
        "
        @change="languageChange"
      />
      Danish
    </label>
    <label for="norwegian">
      <pn-checkbox
        checkboxid="norwegian"
        :checked="
          framesForGroup[0]?.languages &&
          framesForGroup[0].languages.includes('norwegian')
        "
        @change="languageChange"
      />
      Norwegian
    </label>
  </p>
  <p>
    <label for="hidden">
      <pn-checkbox
        checkboxid="hidden"
        :checked="framesForGroup[0]?.hidden"
        @change="firstFrame.hidden = $event.target.checked"
      />
      Completely hide frame
    </label>
  </p>
  <p>Category: (used to hide seasonal frames)</p>

  <select v-model="selectedCategory">
    <option disabled>Select category</option>
    <option v-for="c in categories" :key="c.categoryId" :value="c">
      {{ c.swedish || c.english || c.danish || c.norwegian }}
    </option>
  </select>

  <h3>Frame images</h3>

  <div
    class="flex justify-space-between frame-row"
    v-for="frame in framesForGroup"
    :key="frame.frameId"
  >
    <p>
      <strong>#{{ frame.frameId }}</strong>
    </p>
    <div class="icon">
      <img :src="getImageUrl(frame)" />
    </div>

    <div>
      <pn-file-upload
        name="Upload frame"
        accept=".png"
        max-size="5mb"
        disabled="false"
        limit="1"
        helpertext="Upload image to replace (Image dimensions 2539 * 1807 px, transparent background)."
        @filesAdded="filesAdded($event, frame.frameId)"
      >
      </pn-file-upload>
    </div>
  </div>

  <h3>Alternative Text</h3>
  <div>
    <p>
      <pn-input
        placeholder="Swedish Alternative Text (required *)"
        v-model="swedishAltText"
        :error="swedishAltTextError"
      ></pn-input>
    </p>
    <p>
      <pn-input
        placeholder="English Alternative Text (required *)"
        v-model="englishAltText"
        :error="englishAltTextError"
      ></pn-input>
    </p>
  </div>
  <div>
    <span v-show="canSave" style="display: block; margin-top: 2rem">
      <pn-button :loading="saving" @click="uploadAllFiles"
        >Save changes</pn-button
      >
    </span>
  </div>
</template>

<style lang="scss" scoped>
pn-file-upload {
  .pn-file-upload-container {
    width: 400px;
  }
  pn-icon svg {
    width: 6rem;
    height: 6rem;
  }
}

.icon {
  min-width: 200px;
  min-height: 200px;
  padding: 1rem;

  img {
    border: 1px dashed gray;
    border-radius: 1rem;
    width: 200px;
  }
}

.frame-row {
  gap: 30px;
  margin-bottom: 15px;
}
</style>
